<template>
  <q-card flat bordered>
    <q-card-section class="row items-center justify-between wrap q-pb-none">
      <span class="text-h5 q-mb-md">{{ $t('pages.doctors.title') }}</span>

      <div class="row items-center wrap">
        <q-btn-toggle
          v-model="doctorsFilter"
          toggle-color="primary"
          text-color="primary"
          unelevated
          class="q-mb-md q-mr-md"
          no-caps
          :options="[
            { label: $t('pages.doctors.filter.enabled'), value: true },
            { label: $t('pages.doctors.filter.disabled'), value: false },
          ]"
        />

        <q-input
          v-model="search"
          :placeholder="$t('pages.doctors.searchPlaceholder')"
          type="search"
          debounce="1000"
          class="q-mb-md q-mr-md"
          outlined
          dense
        >
          <template v-slot:append>
            <q-icon
              v-if="search !== ''"
              name="close"
              class="cursor-pointer"
              @click="search = ''"
            />

            <q-icon v-if="search === ''" name="search" />
          </template>
        </q-input>

        <q-btn
          v-if="$isAllowed('dealer/doctors/create')"
          :label="$t('pages.doctors.addButtonTitle')"
          color="primary"
          class="q-mb-md"
          outline
          @click="openModalForAddingDoctor"
        />
      </div>
    </q-card-section>

    <q-card-section v-if="error">
      <q-banner class="text-white bg-red q-mb-md" rounded>
        {{ error.message }}
      </q-banner>
    </q-card-section>

    <q-table
      v-model:pagination="pagination"
      :columns="tableColumns"
      :rows="formattedRows"
      :rows-per-page-options="[10, 20, 50, 100]"
      :loading="isLoading"
      :loading-label="$t('pages.doctors.loading')"
      :no-data-label="$t('pages.doctors.noItems')"
      row-key="userId"
      bordered
      @request="onRequest"
      @row-click="onRowClick"
    >
      <template v-slot:body-cell-actions="props">
        <q-td :props="props" @click.stop="">
          <q-icon name="more_vert">
            <q-menu>
              <q-list style="min-width: 150px">
                <q-item
                  v-if="$isAllowed('dealer/doctors/update')"
                  clickable
                  v-close-popup
                  @click="openModalForEditingDoctor(props.row)"
                >
                  <q-item-section>
                    {{ $t('pages.doctors.edit') }}
                  </q-item-section>
                </q-item>

                <q-item
                  v-if="$isAllowed('dealer/doctors/delete')"
                  clickable
                  v-close-popup
                  @click="confirmDoctorDeletion(props.row)"
                >
                  <q-item-section>
                    {{ $t('pages.doctors.delete') }}
                  </q-item-section>
                </q-item>
              </q-list>
            </q-menu>
          </q-icon>
        </q-td>
      </template>
    </q-table>
  </q-card>

  <doctor-modal
    ref="doctor-modal"
    @doctor-added="getDoctors"
    @doctor-updated="getDoctors"
  />
</template>

<script>
import { getDoctorsAndCount, deleteDoctorById } from '@/api/doctors';
import DoctorModal from '@/components/DoctorModal.vue';

let initialData = {};

export default {
  components: {
    DoctorModal,
  },
  meta() {
    return {
      title: this.$t('pages.doctors.documentTitle'),
    };
  },
  data() {
    return {
      error: initialData.error,
      doctors: initialData.doctors,
      doctorsCount: initialData.doctorsCount,
      isLoading: false,
      doctorsFilter: true,
      formattedRows: [],
    };
  },
  async beforeRouteEnter(to, from, next) {
    if (!to.query.page || !to.query.limit) {
      return next({
        ...to,
        query: {
          ...to.query,
          page: 1,
          limit: 20,
        },
      });
    } else {
      next();
    }
  },
  async beforeRouteUpdate(to, from, next) {
    if (!to.query.page || !to.query.limit) {
      return next({
        ...to,
        query: {
          ...to.query,
          page: 1,
          limit: 20,
        },
      });
    } else {
      next();
    }
  },
  created() {
    this.getDoctors();
  },
  computed: {
    search: {
      get() {
        return this.$route.query.search;
      },
      set(newValue) {
        this.$router.push({
          ...this.$route,
          query: {
            ...this.$route.query,
            search: newValue,
          },
        });
      },
    },
    tableColumns() {
      return [
        {
          name: 'name',
          field: (row) => row.user.fullName,
          label: this.$t('pages.doctors.tableColumns.name'),
          align: 'left',
        },
        {
          name: 'email',
          field: (row) => row.user.email,
          label: this.$t('pages.doctors.tableColumns.email'),
          align: 'left',
        },
        {
          name: 'phone',
          field: (row) => row.user.contactPhone,
          label: this.$t('pages.doctors.tableColumns.phone'),
          align: 'left',
        },
        {
          name: 'status',
          field: (row) =>
            row.user.isActive
              ? this.$t('pages.doctors.statuses.active')
              : this.$t('pages.doctors.statuses.inactive'),
          label: this.$t('pages.doctors.tableColumns.status'),
          align: 'left',
        },
        {
          name: 'balance',
          field: 'balance',
          format: (value) => this.$numeral(value).format('0,0'),
          label: this.$t('pages.doctors.tableColumns.balance'),
          align: 'center',
        },
        {
          name: 'actions',
          label: this.$t('pages.doctors.tableColumns.actions'),
          align: 'right',
        },
      ];
    },
    pagination: {
      get() {
        return {
          page: +this.$route.query.page,
          rowsPerPage: +this.$route.query.limit,
          rowsNumber: this.doctorsCount,
        };
      },
      set({ page, rowsPerPage }) {
        this.$router.push({
          ...this.$route,
          query: {
            ...this.$route.query,
            page,
            limit: rowsPerPage,
          },
        });
      },
    },
  },
  methods: {
    async getDoctors() {
      try {
        this.error = null;
        this.isLoading = true;

        const { doctors, count } = await getDoctorsAndCount({
          filters: {
            query: this.$route.query.search,
          },
          offset: (+this.$route.query.page - 1) * +this.$route.query.limit,
          limit: +this.$route.query.limit,
        });

        this.doctors = doctors;
        this.doctorsCount = count;
        this.formatRows();
      } catch (error) {
        this.error = error;
      } finally {
        this.isLoading = false;
      }
    },
    formatRows() {
      this.formattedRows = this.doctors.filter(
        (doctor) => doctor.user.isActive === this.doctorsFilter
      );
    },
    onRequest({ pagination: { page, rowsPerPage } }) {
      this.pagination = {
        page,
        rowsPerPage,
      };
    },
    onRowClick(event, doctor) {
      this.openDoctorPage(doctor);
    },
    openDoctorPage(doctor) {
      if (this.$isAllowed('dealer/doctors/get')) {
        this.$router.push({
          name: 'DoctorsShow',
          params: {
            doctorId: doctor.userId,
          },
        });
      }
    },
    openModalForAddingDoctor() {
      this.$refs['doctor-modal'].open();
    },
    openModalForEditingDoctor(doctor) {
      this.$refs['doctor-modal'].open(doctor);
    },
    confirmDoctorDeletion(doctor) {
      this.$q
        .dialog({
          title: this.$t('components.deleteDoctorModal.title'),
          message: this.$t('components.deleteDoctorModal.message', {
            name: doctor.user.fullName,
          }),
          ok: this.$t('components.deleteDoctorModal.okButtonTitle'),
          cancel: this.$t('components.deleteDoctorModal.cancelButtonTitle'),
        })
        .onOk(async () => {
          try {
            await deleteDoctorById(doctor.userId);

            this.getDoctors();
          } catch (error) {
            this.error = error;
          }
        });
    },
  },
  watch: {
    '$route.query': {
      handler() {
        this.getDoctors();
      },
      deep: true,
    },
    doctorsFilter: {
      handler() {
        this.formatRows();
      },
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep(.q-btn-group > .q-btn-item:before) {
  border: 1px solid var(--q-primary);
}
</style>
